import axios from "axios";

const API_URL = import.meta.env.VITE_API_URL || "https://dashboard.lono.co.in/api";

// Create axios instance with base URL
const api = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add a request interceptor to include the token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Analyzer API
export const analyzerAPI = {
  // ✅ Evaluation Board APIs (NEW)
  saveEvaluationData: async (loanId, customerId, data) => {
    try {
      const payload = { loanId, customerId, data };
      const response = await api.post("/evaluation/save", payload);
      return response.data;
    } catch (error) {
      console.error("Error saving evaluation data:", error);
      throw error.response?.data || error.message;
    }
  },

  getEvaluationByLoanId: async (loanId) => {
    try {
      const response = await api.get(`/evaluation/${loanId}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching evaluation data:", error);
      throw error.response?.data || error.message;
    }
  },

  // Get submitted documents for a loan application
  getSubmittedDocuments: async (loanId) => {
    try {
      const response = await api.get(
        `/analyzer/loan/${loanId}/submitted-documents`
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Analyze documents with Perplexity API
  analyzeDocuments: async (analyzerType, documents, loanId, customerId) => {
    try {
      const formData = new FormData();

      // Add documents to FormData - handle both single file and multiple files
      if (Array.isArray(documents)) {
        documents.forEach((file) => {
          formData.append("documents", file);
        });
      } else if (documents) {
        formData.append("documents", documents);
      }

      // Add analyzer type and IDs
      formData.append("type", analyzerType);
      if (loanId) formData.append("loanId", loanId);
      if (customerId) formData.append("customerId", customerId);

      const response = await api.post("/analyzer/analyze", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Analyze submitted documents from database
  analyzeSubmittedDocuments: async (
    analyzerType,
    documentIds,
    loanId,
    customerId
  ) => {
    try {
      const payload = {
        type: analyzerType,
        documentIds: documentIds,
        loanId: loanId,
        customerId: customerId,
      };

      const response = await api.post("/analyzer/analyze-submitted", payload);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Analyze multi-format documents (JPG, PNG, PDF)
  analyzeMultiFormatDocuments: async (
    analyzerType,
    documents,
    loanId,
    customerId
  ) => {
    try {
      const formData = new FormData();

      if (Array.isArray(documents)) {
        documents.forEach((file) => {
          formData.append("documents", file);
        });
      } else if (documents) {
        formData.append("documents", documents);
      }

      formData.append("type", analyzerType);
      if (loanId) formData.append("loanId", loanId);
      if (customerId) formData.append("customerId", customerId);

      const response = await api.post(
        "/analyzer/analyze-multi-format",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Analyze submitted multi-format documents
  analyzeMultiFormatSubmittedDocuments: async (
    analyzerType,
    documentIds,
    loanId,
    customerId
  ) => {
    try {
      const payload = {
        type: analyzerType,
        documentIds: documentIds,
        loanId: loanId,
        customerId: customerId,
      };

      console.log("Sending payload:", payload);
      const response = await api.post(
        "/analyzer/analyze-multi-format-submitted",
        payload
      );
      return response.data;
    } catch (error) {
      console.error("API Error:", error.response?.data || error.message);
      throw error.response?.data || error.message;
    }
  },

  // Analyze PDF documents via URL (for submitted documents)
  analyzePdfUrlDocuments: async (
    analyzerType,
    documentIds,
    loanId,
    customerId
  ) => {
    try {
      const payload = {
        type: analyzerType,
        documentIds: documentIds,
        loanId: loanId,
        customerId: customerId,
      };

      const response = await api.post(
        "/pdf-url-analyzer/analyze-pdf-url",
        payload
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get analyzer report by ID
  getAnalyzerReport: async (reportId) => {
    try {
      const response = await api.get(`/analyzer/report/${reportId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
};
